/**
 * CapacitorBLEAdapter
 * Drop-in replacement for WebBluetoothAdapter from mxw01-thermal-printer.
 * Uses @capacitor-community/bluetooth-le for native Android BLE access.
 *
 * The mxw01-thermal-printer library calls:
 *   adapter.requestDevice()     → scan + pick device
 *   adapter.connect(device)     → connect to GATT
 *   adapter.disconnect(device)  → disconnect
 *   adapter.getCharacteristic(device, serviceUUID, charUUID) → returns char-like object
 *   characteristic.writeValueWithoutResponse(buffer) → write chunk
 */

import { BleClient } from '@capacitor-community/bluetooth-le'

// MXW01 / xofiz printer UUIDs
const SERVICE_UUID = '0000ae30-0000-1000-8000-00805f9b34fb'

export class CapacitorBLEAdapter {
  constructor() {
    this._deviceId = null
    this._initialized = false
  }

  async _ensureInit() {
    if (!this._initialized) {
      await BleClient.initialize({ androidNeverForLocation: true })
      this._initialized = true
    }
  }

  /**
   * Scan for MXW01 printer and return a device-like object.
   * If we already have a paired device ID stored, try connecting directly.
   */
  async requestDevice() {
    await this._ensureInit()

    // Try auto-connect to last known device first
    const savedId = localStorage.getItem('mxw01_device_id')
    if (savedId) {
      try {
        await BleClient.connect(savedId, () => this._onDisconnect())
        this._deviceId = savedId
        console.log('[BLE] Auto-connected to saved device:', savedId)
        return { id: savedId, name: localStorage.getItem('mxw01_device_name') || 'MXW01' }
      } catch (e) {
        console.warn('[BLE] Auto-connect failed, falling back to scan:', e)
        localStorage.removeItem('mxw01_device_id')
      }
    }

    // Scan and let user pick
    const device = await BleClient.requestDevice({
      services: [SERVICE_UUID],
      optionalServices: [],
    })

    await BleClient.connect(device.deviceId, () => this._onDisconnect())
    this._deviceId = device.deviceId

    // Save for next time
    localStorage.setItem('mxw01_device_id', device.deviceId)
    localStorage.setItem('mxw01_device_name', device.name || 'MXW01')

    console.log('[BLE] Connected to:', device.name, device.deviceId)
    return { id: device.deviceId, name: device.name || 'MXW01' }
  }

  /**
   * Connect is a no-op here — we connect during requestDevice.
   */
  async connect(device) {
    // Already connected in requestDevice
    return device
  }

  async disconnect(device) {
    if (device?.id) {
      try {
        await BleClient.disconnect(device.id)
      } catch (e) {
        console.warn('[BLE] Disconnect error:', e)
      }
    }
    this._deviceId = null
  }

  /**
   * Returns a characteristic-like object with writeValueWithoutResponse.
   */
  async getCharacteristic(device, serviceUUID, charUUID) {
    const deviceId = device?.id || this._deviceId
    return {
      writeValueWithoutResponse: async (buffer) => {
        const data = buffer instanceof DataView
          ? new Uint8Array(buffer.buffer)
          : new Uint8Array(buffer)
        await BleClient.writeWithoutResponse(
          deviceId,
          serviceUUID,
          charUUID,
          new DataView(data.buffer)
        )
      }
    }
  }

  _onDisconnect() {
    console.warn('[BLE] Printer disconnected')
    this._deviceId = null
  }

  /**
   * Check if currently connected
   */
  get isConnected() {
    return !!this._deviceId
  }
}